import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import {
  ShieldCheck, Plus, Mail, Key, X, Check, Pencil, UserX, UserCheck, Trash2, AlertTriangle,
} from "lucide-react";

// Reusable modal shell — keeps markup DRY across the 4 dialogs below.
function Modal({ title, icon: Icon, children, onClose, testid }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={onClose} />
      <div data-testid={testid}
        style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "440px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
          <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {Icon && <Icon size={18} />} {title}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Pill({ active }) {
  return (
    <span data-testid={active ? "pill-active" : "pill-inactive"}
      style={{
        fontSize: "0.7rem", padding: "0.15rem 0.5rem", borderRadius: 9999, fontWeight: 600,
        background: active ? "var(--sage-light)" : "#F3F4F6",
        color: active ? "var(--sage)" : "#6B7280",
        border: `1px solid ${active ? "var(--sage-border)" : "#E5E7EB"}`,
      }}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export default function SuperAdminsPage() {
  const { user: me } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // 4 modal states
  const [showCreate, setShowCreate] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);    // admin doc
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/superadmins");
      setAdmins(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const isSelf = (a) => me?.id === a.id;
  const activeCount = admins.filter(a => a.is_active).length;
  const isLastActive = (a) => a.is_active && activeCount <= 1;

  const submitCreate = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/admin/superadmins", {
        email: (form.email || "").trim(),
        name: (form.name || "").trim(),
        password: form.password || "",
      });
      setShowCreate(false); setForm({}); await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const submitRename = async () => {
    setErr(""); setBusy(true);
    try {
      await api.patch(`/admin/superadmins/${renameTarget.id}/email`, { email: (form.email || "").trim() });
      setRenameTarget(null); setForm({}); await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const submitReset = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post(`/admin/superadmins/${resetTarget.id}/reset-password`, { password: form.password || "" });
      setResetTarget(null); setForm({}); await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const toggleActive = async (a) => {
    try {
      if (a.is_active) await api.patch(`/admin/superadmins/${a.id}/deactivate`);
      else             await api.patch(`/admin/superadmins/${a.id}/activate`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const submitDelete = async () => {
    setErr(""); setBusy(true);
    try {
      await api.delete(`/admin/superadmins/${deleteTarget.id}`);
      setDeleteTarget(null); await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div style={{ padding: "2rem 2.5rem", maxWidth: "1100px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
          <div>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "2rem", marginBottom: "0.35rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
              <ShieldCheck size={28} /> Super Admins
            </h1>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", margin: 0 }}>
              Manage platform-wide administrator accounts. Department users are managed from the Dashboard.
            </p>
          </div>
          <button
            data-testid="sa-add-btn"
            onClick={() => { setForm({}); setErr(""); setShowCreate(true); }}
            className="btn-primary"
            style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <Plus size={16} /> Add Super Admin
          </button>
        </div>

        {activeCount <= 1 && admins.length > 0 && (
          <div data-testid="sa-lockout-warning" style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            padding: "0.7rem 1rem", marginBottom: "1.25rem",
            background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "0.5rem",
            color: "#92400E", fontSize: "0.85rem",
          }}>
            <AlertTriangle size={16} />
            Only <strong>one active super admin</strong> remains — add a second before deactivating or deleting this one.
          </div>
        )}

        {loading ? (
          <div style={{ padding: "2rem", color: "var(--text-muted)" }}>Loading…</div>
        ) : (
          <div className="recon-card" style={{ overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th className="table-header">Email</th>
                  <th className="table-header">Name</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Last login</th>
                  <th className="table-header" style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map(a => {
                  const self = isSelf(a);
                  const lastActive = isLastActive(a);
                  return (
                    <tr key={a.id} className="table-row" data-testid={`sa-row-${a.email}`}>
                      <td className="table-cell" style={{ fontWeight: 600 }}>
                        {a.email}
                        {self && <span style={{ marginLeft: "0.4rem", fontSize: "0.7rem", color: "var(--sage)", fontWeight: 600 }}>(you)</span>}
                      </td>
                      <td className="table-cell">{a.name || "—"}</td>
                      <td className="table-cell"><Pill active={a.is_active} /></td>
                      <td className="table-cell" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                        {a.last_login_at ? new Date(a.last_login_at).toLocaleString() : "—"}
                      </td>
                      <td className="table-cell" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <div style={{ display: "inline-flex", gap: "0.35rem" }}>
                          <button
                            data-testid={`sa-rename-${a.email}`}
                            onClick={() => { setForm({ email: a.email }); setErr(""); setRenameTarget(a); }}
                            className="btn-secondary" title="Rename email"
                            style={{ padding: "0.3rem 0.55rem" }}>
                            <Pencil size={12} />
                          </button>
                          <button
                            data-testid={`sa-reset-${a.email}`}
                            onClick={() => { setForm({}); setErr(""); setResetTarget(a); }}
                            className="btn-secondary" title="Reset password"
                            style={{ padding: "0.3rem 0.55rem" }}>
                            <Key size={12} />
                          </button>
                          <button
                            data-testid={`sa-toggle-${a.email}`}
                            onClick={() => toggleActive(a)}
                            disabled={self || (a.is_active && lastActive)}
                            className="btn-secondary"
                            title={self ? "You cannot deactivate yourself" : (a.is_active && lastActive) ? "Last active superadmin — add another first" : (a.is_active ? "Deactivate" : "Reactivate")}
                            style={{ padding: "0.3rem 0.55rem", opacity: (self || (a.is_active && lastActive)) ? 0.4 : 1 }}>
                            {a.is_active ? <UserX size={12} /> : <UserCheck size={12} />}
                          </button>
                          <button
                            data-testid={`sa-delete-${a.email}`}
                            onClick={() => { setErr(""); setDeleteTarget(a); }}
                            disabled={self || (a.is_active && lastActive)}
                            className="btn-secondary"
                            title={self ? "You cannot delete yourself" : (a.is_active && lastActive) ? "Last active superadmin — cannot delete" : "Delete permanently"}
                            style={{ padding: "0.3rem 0.55rem", color: "#991B1B", borderColor: "#FCA5A5", opacity: (self || (a.is_active && lastActive)) ? 0.4 : 1 }}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Create modal ─────────────────────────────────────────────── */}
      {showCreate && (
        <Modal title="Add Super Admin" icon={Plus} testid="sa-create-modal"
          onClose={() => { setShowCreate(false); setErr(""); }}>
          <label style={{ fontSize: "0.8rem", fontWeight: 500, display: "block", marginBottom: "0.25rem" }}>Email</label>
          <input data-testid="sa-create-email" type="email" value={form.email || ""}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="admin@yourcompany.com"
            className="recon-input" style={{ width: "100%", marginBottom: "0.75rem" }} />
          <label style={{ fontSize: "0.8rem", fontWeight: 500, display: "block", marginBottom: "0.25rem" }}>Display name</label>
          <input data-testid="sa-create-name" value={form.name || ""}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Jane Doe"
            className="recon-input" style={{ width: "100%", marginBottom: "0.75rem" }} />
          <label style={{ fontSize: "0.8rem", fontWeight: 500, display: "block", marginBottom: "0.25rem" }}>Password (min. 8 characters)</label>
          <input data-testid="sa-create-password" type="password" value={form.password || ""}
            onChange={e => setForm({ ...form, password: e.target.value })}
            className="recon-input" style={{ width: "100%", marginBottom: "0.75rem" }} />
          {err && <div data-testid="sa-error" style={{ padding: "0.5rem 0.7rem", background: "#FEE2E2", color: "#991B1B", borderRadius: "0.375rem", fontSize: "0.8rem", marginBottom: "0.75rem" }}>{err}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <button onClick={() => { setShowCreate(false); setErr(""); }} className="btn-secondary">Cancel</button>
            <button data-testid="sa-create-submit" onClick={submitCreate} disabled={busy} className="btn-primary">
              {busy ? "Creating…" : "Create"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Rename modal ─────────────────────────────────────────────── */}
      {renameTarget && (
        <Modal title="Rename Super Admin email" icon={Mail} testid="sa-rename-modal"
          onClose={() => { setRenameTarget(null); setErr(""); }}>
          <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", margin: "0 0 0.75rem" }}>
            Current email: <strong>{renameTarget.email}</strong>
          </p>
          <label style={{ fontSize: "0.8rem", fontWeight: 500, display: "block", marginBottom: "0.25rem" }}>New email</label>
          <input data-testid="sa-rename-email" type="email" value={form.email || ""}
            onChange={e => setForm({ ...form, email: e.target.value })}
            className="recon-input" style={{ width: "100%", marginBottom: "0.75rem" }} />
          {err && <div data-testid="sa-error" style={{ padding: "0.5rem 0.7rem", background: "#FEE2E2", color: "#991B1B", borderRadius: "0.375rem", fontSize: "0.8rem", marginBottom: "0.75rem" }}>{err}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <button onClick={() => { setRenameTarget(null); setErr(""); }} className="btn-secondary">Cancel</button>
            <button data-testid="sa-rename-submit" onClick={submitRename} disabled={busy} className="btn-primary">
              {busy ? "Renaming…" : "Rename"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Reset password modal ─────────────────────────────────────── */}
      {resetTarget && (
        <Modal title="Reset Super Admin password" icon={Key} testid="sa-reset-modal"
          onClose={() => { setResetTarget(null); setErr(""); }}>
          <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", margin: "0 0 0.75rem" }}>
            For <strong>{resetTarget.email}</strong>
          </p>
          <label style={{ fontSize: "0.8rem", fontWeight: 500, display: "block", marginBottom: "0.25rem" }}>New password (min. 8 characters)</label>
          <input data-testid="sa-reset-password" type="password" value={form.password || ""}
            onChange={e => setForm({ ...form, password: e.target.value })}
            className="recon-input" style={{ width: "100%", marginBottom: "0.75rem" }} />
          {err && <div data-testid="sa-error" style={{ padding: "0.5rem 0.7rem", background: "#FEE2E2", color: "#991B1B", borderRadius: "0.375rem", fontSize: "0.8rem", marginBottom: "0.75rem" }}>{err}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <button onClick={() => { setResetTarget(null); setErr(""); }} className="btn-secondary">Cancel</button>
            <button data-testid="sa-reset-submit" onClick={submitReset} disabled={busy} className="btn-primary">
              {busy ? "Resetting…" : "Reset password"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Delete modal ─────────────────────────────────────────────── */}
      {deleteTarget && (
        <Modal title="Delete Super Admin" icon={Trash2} testid="sa-delete-modal"
          onClose={() => { setDeleteTarget(null); setErr(""); }}>
          <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", margin: "0 0 0.75rem" }}>
            Permanently delete <strong>{deleteTarget.email}</strong>? This cannot be undone. Audit trail entries are kept.
          </p>
          {err && <div data-testid="sa-error" style={{ padding: "0.5rem 0.7rem", background: "#FEE2E2", color: "#991B1B", borderRadius: "0.375rem", fontSize: "0.8rem", marginBottom: "0.75rem" }}>{err}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <button onClick={() => { setDeleteTarget(null); setErr(""); }} className="btn-secondary">Cancel</button>
            <button data-testid="sa-delete-submit" onClick={submitDelete} disabled={busy}
              className="btn-primary" style={{ background: "#991B1B" }}>
              {busy ? "Deleting…" : "Delete"}
            </button>
          </div>
        </Modal>
      )}
    </Layout>
  );
}
